// app-components/investor-profile/co-investment-network/PartnerSwitcher.jsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import PartnerCard from './PartnerCard';
import NetworkCTA from './NetworkCTA';
import { useMembershipAccess } from '../../../../hooks/membership';

const PartnerSwitcher = ({ partners, partnerNames, dealInfo, hiddenCount }) => {
    const { t } = useTranslation();
    const { isFree } = useMembershipAccess();
    const [currentIndex, setCurrentIndex] = useState(0);

    const handlePrev = () => {
        if (partners.length <= 1) return;
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : partners.length - 1));
    };

    const handleNext = () => {
        if (partners.length <= 1) return;
        setCurrentIndex((prev) => (prev < partners.length - 1 ? prev + 1 : 0));
    };

    if (!partners || partners.length === 0) return null;

    const currentPartner = partners[currentIndex];
    const hasMultiplePartners = partners.length > 1;

    return (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(18rem,0.9fr)] gap-6 items-start">
            {/* Left Column - Partner Card with Navigation */}
            <div className="min-w-0 space-y-4">
                {/* Always show navigation controls, but disable them for a single partner. */}
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-2 items-stretch mb-4">
                    <button
                        onClick={handlePrev}
                        disabled={!hasMultiplePartners}
                        className={`min-w-0 inline-flex min-h-10 items-center justify-center gap-2 px-3 py-2 rounded-md transition-colors ${
                            hasMultiplePartners
                                ? 'bg-gray-100 hover:bg-gray-200 cursor-pointer'
                                : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        }`}
                        aria-label="Previous partner"
                    >
                        <ChevronLeft size={20} className="shrink-0" />
                        <span className="min-w-0 text-sm font-medium leading-tight text-center break-words">{t('pageInvProf.previous') || 'Previous'}</span>
                    </button>

                    <div className="flex min-h-10 items-center justify-center px-2 text-sm font-medium text-gray-600 whitespace-nowrap">
                        {currentIndex + 1} / {partners.length}
                    </div>

                    <button
                        onClick={handleNext}
                        disabled={!hasMultiplePartners}
                        className={`min-w-0 inline-flex min-h-10 items-center justify-center gap-2 px-3 py-2 rounded-md transition-colors ${
                            hasMultiplePartners
                                ? 'bg-gray-100 hover:bg-gray-200 cursor-pointer'
                                : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        }`}
                        aria-label="Next partner"
                    >
                        <span className="min-w-0 text-sm font-medium leading-tight text-center break-words">{t('pageInvProf.next') || 'Next'}</span>
                        <ChevronRight size={20} className="shrink-0" />
                    </button>
                </div>

                {/* Current Partner Card */}
                <PartnerCard
                    partner={currentPartner}
                    partnerNames={partnerNames}
                    dealInfo={dealInfo}
                    showRecentIds={!isFree}
                />
            </div>

            {/* Right Column - CTA and Description */}
            <div className="min-w-0 space-y-4">
                {/* Description Text */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700 leading-relaxed">
                        {t('pageInvProf.coInvestDescription')}
                    </p>
                </div>

                {/* Show upgrade CTA only for free users with hidden partners. */}
                {isFree && hiddenCount > 0 && (
                    <NetworkCTA hiddenCount={hiddenCount} />
                )}
            </div>
        </div>
    );
};

export default PartnerSwitcher;
